import type { ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'

import type { GroupSelectGroupedItems } from '@/types/components/select'
import type { PopoverProps } from '@radix-ui/react-popover'
import { Popover } from '../common/popover'
import { FilterSelectTrigger } from './filterSelect'
import { GroupedFilterSelectContent, type GroupedFilterSelectContentProps } from './groupedFilterSelectContent'

type GroupedFilterSelectProps = GroupedFilterSelectContentProps & Pick<PopoverProps, 'modal'>

export const GroupedFilterSelect = ({ groupedItems, className, modal, ...props }: GroupedFilterSelectProps) => {
  const [selectedItemsSnapshot, setSelectedItemsSnapshot] = useState<string[]>([])
  const [shouldSort, setShouldSort] = useState(false)

  const itemsPresent = Object.values(groupedItems).reduce((sum, curr) => sum + curr.items.length, 0) > 0
  const sortedGroupedItems = useMemo(() => {
    if (!shouldSort) return groupedItems

    const sortedEntries = Object.entries(groupedItems)
      .map(([groupName, group]) => {
        const selected = group.items.filter((item) => selectedItemsSnapshot.includes(item))
        const unselected = group.items.filter((item) => !selectedItemsSnapshot.includes(item))

        return [groupName, { ...group, items: [...selected, ...unselected] }] as const
      })
      .sort(([, groupA], [, groupB]) => {
        const selectedInA = groupA.items.filter((item) => selectedItemsSnapshot.includes(item)).length
        const selectedInB = groupB.items.filter((item) => selectedItemsSnapshot.includes(item)).length

        if (selectedInA !== selectedInB) {
          return selectedInB - selectedInA
        }

        return 0
      })

    return Object.fromEntries(sortedEntries) as GroupSelectGroupedItems
  }, [groupedItems, selectedItemsSnapshot, shouldSort])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setSelectedItemsSnapshot([...props.selectedItems])
        setShouldSort(props.selectedItems.length > 0)
      }
    },
    [props.selectedItems]
  )

  return (
    <Popover modal={modal} onOpenChange={handleOpenChange}>
      <FilterSelectTrigger className={className}>
        <div className="flex w-full max-w-full items-center gap-2 truncate">
          <SelectTriggerContent {...props} itemsPresent={itemsPresent} />
        </div>
      </FilterSelectTrigger>
      <GroupedFilterSelectContent groupedItems={sortedGroupedItems} {...props} />
    </Popover>
  )
}

interface SelectTriggerContentProps extends Omit<GroupedFilterSelectProps, 'groupedItems' | 'setSelectedItems'> {
  itemsPresent: boolean
}

const SelectTriggerContent = ({
  selectedItems,
  name,
  renderItemPrefix,
  itemsPresent,
}: SelectTriggerContentProps): ReactNode => {
  const itemsLength = selectedItems.length

  if (itemsLength === 0 || !itemsPresent) {
    return <span className="first-letter:uppercase">{name}</span>
  }

  if (itemsLength > 1) {
    return <span>{itemsLength} selected</span>
  }

  return (
    <>
      {renderItemPrefix?.(selectedItems[0])}
      <span>{selectedItems[0]}</span>
    </>
  )
}
